import { RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS } from "~/screens/Scheduling/PersonalInformation/components/VisitForm/constants";
import { FILL_OPTION, VISIT_TYPES } from "~/services/scheduling/constants";
import type { StaffScheduling, VisitType } from "~/services/scheduling/types";
import { INSURANCE_NOT_FOUND_OPTION } from "~/shared.types";
import type { StaffPatientInfoFormData } from "./staff-booking-schemas";

type StaffBookingPayloadContext = {
  userId: string | null;
  email: string;
  hasEhrId: boolean;
  visitType: VisitType;
  stateId: number;
  insuranceId: number | null;
  providerId: number;
  visitDateTime: string;
  bookingFlowId?: string;
};

const buildInsuranceFields = (
  formData: StaffPatientInfoFormData,
  hasPlan: boolean,
): Partial<StaffScheduling> => {
  if (formData.fillInsuranceInfo !== FILL_OPTION.NOW) return {};

  const fields: Partial<StaffScheduling> = {
    insuranceRelationshipToPolicyHolder:
      formData.relationshipToPolicyHolder || undefined,
    insurancePolicyNumber: formData.memberId || undefined,
    insuranceGroupNumber: formData.groupNumber || undefined,
    insuranceCardFront: formData.insuranceCardFront ?? undefined,
    insuranceCardBack: formData.insuranceCardBack ?? undefined,
  };

  if (!hasPlan) {
    fields.insuranceCompanyName = formData.insuranceCompanyName || undefined;
  }

  if (
    formData.relationshipToPolicyHolder !==
    RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS.SELF
  ) {
    fields.insurancePolicyHolderFirstName =
      formData.policyHolderFirstName || undefined;
    fields.insurancePolicyHolderLastName =
      formData.policyHolderLastName || undefined;
    fields.insurancePolicyHolderDob =
      formData.policyHolderDateOfBirth || undefined;
  }

  return fields;
};

const buildFollowUpFields = (
  formData: StaffPatientInfoFormData,
): Partial<StaffScheduling> => ({
  addressChanged: formData.fillAddressInfo === FILL_OPTION.NOW,
  insuranceChanged: formData.fillInsuranceInfo === FILL_OPTION.NOW,
});

export const buildStaffBookingPayload = (
  formData: StaffPatientInfoFormData,
  context: StaffBookingPayloadContext,
): StaffScheduling => {
  const insuranceId =
    context.insuranceId === INSURANCE_NOT_FOUND_OPTION.value
      ? undefined
      : (context.insuranceId ?? undefined);
  const isFollowUp = context.visitType === VISIT_TYPES.FOLLOW_UP;

  return {
    visitDateTime: context.visitDateTime,
    appointmentType: context.visitType,
    providerId: context.providerId,
    stateId: context.stateId,
    insuranceId,
    patientAddressOne: formData.homeAddress1 || undefined,
    patientAddressTwo: formData.homeAddress2 || undefined,
    patientCity: formData.city || undefined,
    patientStateId: formData.state ? parseInt(formData.state) : undefined,
    patientZipCode: formData.zipCode || undefined,
    bookingNotes: formData.bookingNotes || undefined,
    bookingFlowId: context.bookingFlowId,
    ...buildInsuranceFields(formData, insuranceId !== undefined),
    ...(isFollowUp && buildFollowUpFields(formData)),
    ...(context.userId
      ? { userId: context.userId }
      : { patientEmail: context.email }),
    ...(!context.hasEhrId &&
      !isFollowUp && {
        patientFirstName: formData.firstName,
        patientLastName: formData.lastName,
        patientDob: formData.dateOfBirth,
        patientSexAtBirth: formData.sexAtBirth,
        patientMobilePhoneNumber: formData.mobilePhone,
        patientHowDidYouHear: formData.referralSource || undefined,
      }),
  };
};
